import React, { useState, useEffect } from "react";
import styles from "./RoomManagement.module.css";
import { compressImages, getImageUrl } from "../../utils/imageUtils";
import { parseLatLng, isValidLatLng } from "../../utils/coordinates";

const RoomTypeEditModal = ({ open, onClose, roomType, onSave }) => {
  const [form, setForm] = useState({ ...roomType });
  const [isCompressing, setIsCompressing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [coordsInput, setCoordsInput] = useState("");
  const [coordsError, setCoordsError] = useState("");
  useEffect(() => {
    setForm({ ...roomType });
  }, [roomType]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleParseCoords = () => {
    const parsed = parseLatLng(coordsInput);
    if (!parsed) {
      setCoordsError("Couldn't find coordinates in that. Paste a Google Maps link or \"lat, lng\".");
      return;
    }
    setForm((prev) => ({ ...prev, latitude: parsed.lat, longitude: parsed.lng }));
    setCoordsError("");
    setCoordsInput("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create FormData for file uploads
    const formData = new FormData();
    
    // Add all form fields except images
    Object.entries(form).forEach(([key, value]) => {
      if (key === 'images') return; // Handle images separately
      
      if (typeof value === 'boolean') {
        formData.append(key, value ? 1 : 0);
      } else {
        formData.append(key, value || '');
      }
    });
    
    // Handle images: separate existing (string) images from new (File) images
    const existingImages = [];
    const newFiles = [];
    
    if (Array.isArray(form.images)) {
      form.images.forEach(img => {
        if (typeof img === 'string') {
          existingImages.push(img);
        } else {
          newFiles.push(img);
        }
      });
    }
    
    // Add existing images as strings
    existingImages.forEach(img => {
      formData.append('existing_images[]', img);
    });
    
    // Add new files
    newFiles.forEach(file => {
      formData.append('images[]', file);
    });

    setIsSaving(true);
    try {
      await onSave(formData);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal} style={{ maxWidth: 800 }} role="dialog" aria-modal="true" aria-labelledby="edit-roomtype-heading">
        <div className={styles.modalStickyTop}>
          <h2 id="edit-roomtype-heading">Edit Room Type</h2>
        </div>
        <form onSubmit={handleSubmit}>
          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Basics</h3>
            <div className={styles.formGroup}>
              <label>Title</label>
              <input name="title" value={form.title || ''} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Description</label>
              <textarea name="description" value={form.description || ''} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Price Per Night</label>
              <input name="price_per_night" type="number" min="0" value={form.price_per_night || ''} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Capacity</label>
              <input name="capacity" type="number" min="1" value={form.capacity || ''} onChange={handleChange} required />
            </div>
            <div className={styles.formGroup}>
              <label>Number of Rooms</label>
              {typeof roomType.number_of_rooms !== 'undefined' && (
                <div style={{ marginBottom: 4, color: '#888', fontSize: 13 }}>
                  Current: <b>{roomType.number_of_rooms}</b>
                </div>
              )}
              <input
                name="number_of_rooms"
                type="number"
                min="1"
                value={form.number_of_rooms || ''}
                onChange={handleChange}
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>Images</label>
              <input
                name="images"
                type="file"
                accept="image/*"
                multiple
                disabled={isCompressing}
                onChange={async (e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    setIsCompressing(true);
                    try {
                      const compressedFiles = await compressImages(Array.from(e.target.files));
                      setForm(prev => ({
                        ...prev,
                        images: [
                          ...Array.isArray(prev.images) ? prev.images.filter(img => typeof img === 'string') : [],
                          ...compressedFiles
                        ]
                      }));
                    } catch (error) {
                      console.error('Error compressing images:', error);
                      // Fallback to original files if compression fails
                      setForm(prev => ({
                        ...prev,
                        images: [
                          ...Array.isArray(prev.images) ? prev.images.filter(img => typeof img === 'string') : [],
                          ...Array.from(e.target.files)
                        ]
                      }));
                    } finally {
                      setIsCompressing(false);
                    }
                  }
                }}
              />
              {isCompressing && (
                <div className={styles.compressingNote}>Compressing images…</div>
              )}
              {Array.isArray(form.images) && form.images.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap', minHeight: 70 }}>
                  {form.images.map((img, idx) => {
                    const imageUrl = typeof img === 'string' ? getImageUrl(img) : URL.createObjectURL(img);
                    return (
                      <div key={idx} className={styles.imagePreview}>
                        <img
                          src={imageUrl}
                          alt={`Room type image ${idx + 1}`}
                          onError={(e) => {
                            e.target.style.background = '#f5f5f5';
                            e.target.alt = 'Image not available';
                          }}
                        />
                        <button
                          type="button"
                          className={styles.removeImageBtn}
                          onClick={() => {
                            setForm(prev => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== idx)
                            }));
                          }}
                          aria-label={`Remove image ${idx + 1}`}
                          title="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Location</h3>
            <div className={styles.coordsRow}>
              <div>
                <label>
                  Latitude
                  <input
                    name="latitude"
                    type="number"
                    step="any"
                    min="-90"
                    max="90"
                    value={form.latitude || ''}
                    onChange={handleChange}
                  />
                </label>
              </div>
              <div>
                <label>
                  Longitude
                  <input
                    name="longitude"
                    type="number"
                    step="any"
                    min="-180"
                    max="180"
                    value={form.longitude || ''}
                    onChange={handleChange}
                  />
                </label>
              </div>
            </div>
            <div className={styles.coordsHelper}>
              <input
                type="text"
                placeholder="Paste a Google Maps link or &quot;lat, lng&quot;"
                value={coordsInput}
                onChange={(e) => { setCoordsInput(e.target.value); setCoordsError(""); }}
                aria-label="Paste coordinates or Google Maps link"
              />
              <button type="button" className={styles.parseBtn} onClick={handleParseCoords} disabled={!coordsInput.trim()}>
                Fill in
              </button>
            </div>
            {coordsError && <p className={`${styles.coordsHint} ${styles.coordsHintError}`}>{coordsError}</p>}
            {form.latitude !== "" && form.latitude != null && form.longitude !== "" && form.longitude != null && isValidLatLng(Number(form.latitude), Number(form.longitude)) && (
              <a
                className={styles.mapLink}
                href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`}
                target="_blank"
                rel="noreferrer"
              >
                View on Google Maps →
              </a>
            )}
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Add-on Pricing</h3>
            <div className={styles.optionToggle}>
              <input
                type="checkbox"
                name="breakfast_enabled"
                checked={!!form.breakfast_enabled}
                onChange={handleChange}
                id="edit_breakfast_enabled"
              />
              <label htmlFor="edit_breakfast_enabled">Breakfast Option Available</label>
            </div>
            {form.breakfast_enabled && (
              <div className={styles.optionPriceField}>
                <label htmlFor="edit_breakfast_price">Breakfast Price (₦ per person per night)</label>
                <input
                  name="breakfast_price"
                  id="edit_breakfast_price"
                  type="number"
                  min="0"
                  value={form.breakfast_price || ''}
                  onChange={handleChange}
                  required={form.breakfast_enabled}
                />
              </div>
            )}
            <div className={styles.optionToggle} style={{ marginTop: 12 }}>
              <input
                type="checkbox"
                name="airport_conveyance_enabled"
                checked={!!form.airport_conveyance_enabled}
                onChange={handleChange}
                id="edit_airport_conveyance_enabled"
              />
              <label htmlFor="edit_airport_conveyance_enabled">Airport Conveyance Option Available</label>
            </div>
            {form.airport_conveyance_enabled && (
              <>
                <div className={styles.optionPriceField}>
                  <label htmlFor="edit_airport_pickup_price">Airport Pickup Price (₦)</label>
                  <input
                    name="airport_pickup_price"
                    id="edit_airport_pickup_price"
                    type="number"
                    min="0"
                    value={form.airport_pickup_price || ''}
                    onChange={handleChange}
                    required={form.airport_conveyance_enabled}
                  />
                </div>
                <div className={styles.optionPriceField}>
                  <label htmlFor="edit_airport_dropoff_price">Airport Drop-off Price (₦)</label>
                  <input
                    name="airport_dropoff_price"
                    id="edit_airport_dropoff_price"
                    type="number"
                    min="0"
                    value={form.airport_dropoff_price || ''}
                    onChange={handleChange}
                    required={form.airport_conveyance_enabled}
                  />
                </div>
                <div className={styles.optionPriceField}>
                  <label htmlFor="edit_airport_both_price">Airport Both (Pickup & Drop-off) Price (₦)</label>
                  <input
                    name="airport_both_price"
                    id="edit_airport_both_price"
                    type="number"
                    min="0"
                    value={form.airport_both_price || ''}
                    onChange={handleChange}
                    required={form.airport_conveyance_enabled}
                  />
                </div>
              </>
            )}
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>Amenities</h3>
            <div className={styles.amenitiesGrid} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
            <div className={styles.optionToggle}>
              <input
                type="checkbox"
                name="king_size_bed"
                checked={!!form.king_size_bed}
                onChange={handleChange}
                id="edit_king_size_bed"
              />
              <label htmlFor="edit_king_size_bed">King-size bed</label>
            </div>
            <div className={styles.optionToggle}>
              <input
                type="checkbox"
                name="wifi"
                checked={!!form.wifi}
                onChange={handleChange}
                id="edit_wifi"
              />
              <label htmlFor="edit_wifi">Wifi</label>
            </div>
            <div className={styles.optionToggle}>
              <input
                type="checkbox"
                name="private_bathroom"
                checked={!!form.private_bathroom}
                onChange={handleChange}
                id="edit_private_bathroom"
              />
              <label htmlFor="edit_private_bathroom">Private bathroom</label>
            </div>
            <div className={styles.optionToggle}>
              <input
                type="checkbox"
                name="smart_tv"
                checked={!!form.smart_tv}
                onChange={handleChange}
                id="edit_smart_tv"
              />
              <label htmlFor="edit_smart_tv">Smart TV</label>
            </div>
            <div className={styles.optionToggle}>
              <input
                type="checkbox"
                name="air_conditioning"
                checked={!!form.air_conditioning}
                onChange={handleChange}
                id="edit_air_conditioning"
              />
              <label htmlFor="edit_air_conditioning">Air conditioning</label>
            </div>
            </div>
          </div>
          <div className={styles.modalStickyBottom}>
            <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={isSaving}>Cancel</button>
            <button type="submit" className={styles.saveBtn} disabled={isSaving || isCompressing}>
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RoomTypeEditModal;
